import { faker } from "@faker-js/faker/locale/en_US";
import moment, { Moment } from "moment";
import { ITrip, IPlayerStatsForTimePeriod, IPlayerStatsComp, INetRevenue, INetRevenueAnalysis } from "services/models";
import { add, format } from "date-fns";
import { randomNullableNumber } from "./host-data";
const shortMonths = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function monthString(date: Date, includeYear: boolean = false): string {
  let value = `${shortMonths[date.getMonth()]} ${String(date.getDate())}`;
  if (includeYear) {
    value += ` ${date.getFullYear()}`;
  }
  return value;
}

function round(value: number, places: number = 2): number {
  const power = places * 10;
  return Math.round(value * power) / power;
}

function daysIntoYear(date: Date): number {
  return (
    (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) - Date.UTC(date.getFullYear(), 0, 1)) /
    24 /
    60 /
    60 /
    1000
  );
}

const periodToDays = (period: string): number => {
  const daysAgo = parseInt(period);
  if (isNaN(daysAgo)) {
    switch (period) {
      case "YTD":
        return daysIntoYear(new Date());
      default:
        return 7;
    }
  }
  return daysAgo;
};

const mockTrips = (period: string): ITrip[] => {
  const days = getRandomInt(0, periodToDays(period));
  return Array(days)
    .fill(1)
    .map((_, index) => {
      const date = new Date();
      date.setDate(date.getDate() - index);
      const totalIn = getRandomInt(10, 100);
      return {
        date: date.toISOString(),
        totalIn: totalIn,
        theoWin: getRandomInt(100, 10000),
        actualWinLoss: getRandomInt(100, 10000),
        averageBet: round(totalIn / getRandomInt(1, 10)),
        timePlayed: getRandom(["10 min", "1 hr", "2 hr", "2hr 15min"]),
        dominance: getRandom(["Slots", "Table Games"]),
        gameOfDominance: getRandom(["Wizard of Oz", "Walking Dead"]),
        areaOfDominance: getRandom(["AA", "DD", "TT"]),
        offersRedeemed: getRandomArray(0, 5).map((t, index) => `${index}`),
      };
    });
};

export const mockNetRevenue = (period: string): INetRevenueAnalysis => {
  const today = new Date();
  const previous = add(today, { days: Number(-period) });
  return {
    dateRange: {
      current: {
        start: previous.toISOString(),
        end: today.toISOString(),
      },
      previous: {
        start: add(previous, { days: Number(-period) }).toISOString(),
        end: previous.toISOString(),
      },
    },
    totalTheoWin: getRandomInt(100, 400),
    totalNetWinLoss: getRandomInt(100, 400),
    totalFreeplayUsed: getRandomInt(100, 400),
    netRevenues: getRandomArray(2, 25)
      .fill(1)
      .map((_) => {
        return {
          theoWin: getRandomInt(100, 400),
          netWinLoss: getRandomInt(100, 400),
          freeplayUsed: getRandomInt(100, 400),
          playDate: format(faker.date.past({ years: 1 }), "MM/dd/yyyy"),
        };
      }),
  };
};

export const mockTripsForPeriods: { [key in string]: ITrip[] } = {
  "1": mockTrips("1"),
  "7": mockTrips("7"),
  YTD: mockTrips("YTD"),
};

const mockPeriod = (period: string): IPlayerStatsForTimePeriod => {
  let daysAgo = periodToDays(period);
  let previous: { start: Date; end: Date } | undefined = undefined;
  if (period === "YTD") {
    const lastYearStart = new Date();
    lastYearStart.setFullYear(lastYearStart.getFullYear() - 1);
    lastYearStart.setMonth(0);
    lastYearStart.setDate(1);
    const lastYearEnd = new Date();
    lastYearEnd.setFullYear(lastYearEnd.getFullYear() - 1);
    previous = {
      start: lastYearStart,
      end: lastYearEnd,
    };
  }
  const currentEnd = new Date();
  const currentStart = new Date();
  currentStart.setDate(currentStart.getDate() - daysAgo);

  const previousEnd = previous?.end ?? currentStart;
  let previousStart = previous?.start;
  if (!previousStart) {
    previousStart = new Date(currentStart);
    previousStart.setDate(previousStart.getDate() - daysAgo);
  }

  const includeYear = currentEnd.getFullYear() !== previousEnd.getFullYear();

  const totalIn = getRandomInt(100, 1000);
  const profit = getRandomInt(100, 1000);
  const availableOffers = getRandomInt(10, 20);

  const latestComps: IPlayerStatsComp[] = [];
  const compsToShow = 5;
  for (let i = 0; i < compsToShow; i++) {
    latestComps.push({
      id: getRandomInt(100, 1000),
      issued: moment().format("d/mm/yyyy - hh:mm:ss a"),
      issuedBy: "N. Mata",
      type: "Retail",
      name: "$100 High Limit Thrill Bet",
    });
  }
  return {
    dateRange: {
      current: {
        start: monthString(currentStart),
        end: monthString(currentEnd, includeYear),
      },
      previous: {
        start: monthString(previousStart),
        end: monthString(previousEnd, includeYear),
      },
    },
    stats: {
      adt: randomNullableNumber(100, 10000),
      avt: getRandomDecimal(-10, 10),
      cvScore: getRandomInt(1, 12),
      cvTrend: getRandom(["up", "down"]),
      totalIn: totalIn,
      previousTotalIn: totalIn - getRandomInt(-totalIn, totalIn),
      profit: profit,
      previousProfit: profit - getRandomInt(-profit, profit),
    },
    previousTrips: mockTripsForPeriods[period].map((t) => ({
      ...t,
      date: monthString(new Date(t.date), true),
    })),
    comps: {
      totalIssued: getRandomInt(1000, 2000),
      totalSettled: getRandomInt(100, 1000),
      latest: latestComps[0],
    },
    offers: {
      availableOfferCount: availableOffers,
      redeemedOfferCount: getRandomInt(0, availableOffers),
      expiredOfferCount: getRandomInt(0, 10),
      expiringSoonCount: getRandomInt(0, 10),
      totalLiability: getRandomInt(1000, 10000),
    },
  };
};

export const mockPeriods: { [key in string]: IPlayerStatsForTimePeriod } = {
  "1": mockPeriod("1"),
  "7": mockPeriod("7"),
  YTD: mockPeriod("YTD"),
};

export function getRandomNegativeNumber(num: number) {
  return num * (faker.datatype.boolean() ? 1 : -1);
}

export function getRandomDecimal(min: number, max: number) {
  return Math.random() * (max - min) + min;
}

export function getRandomInt(min: number, max: number) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min) + min); //The maximum is exclusive and the minimum is inclusive
}
export function getRandom<T>(items: T[]) {
  const index = getRandomInt(0, items.length);
  return items[index];
}

export function getRandomArray(min: number, max: number): number[] {
  return Array(getRandomInt(min, max))
    .fill(1)
    .map((value, index) => index + 1);
}

export function getRandomDate(minYear?: number, maxYear?: number): Moment {
  const date = `${minYear && maxYear ? getRandomInt(minYear, maxYear) : moment().year()}-${getRandomInt(1, 12)
    .toString()
    .padStart(2, "0")}-${getRandomInt(1, 29).toString().padStart(2, "0")}`;
  return moment(date);
}

export function getRandomBirthday(): string {
  // const date = faker.date.birthdate({
  //   min: 21,
  //   max: 90,
  //   mode: "age",
  // });
  // return moment(date).toString();
  const date = faker.date.between({ from: "2022-12-01T00:00:00.000Z", to: "2022-12-30T00:00:00.000Z" });
  return moment(date).toString();
}
